import { useState } from 'react';

import { IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button, Dropdown, Surface, Typography } from '@cfa/react-components';
import { IconButton } from '@cfa/react-core';
import { Filter } from '@cfa/system-icons';

import { useLockBody } from '@/hooks/useLockBody';

import { cn } from '@/utils/tailwind';

import { SortOptions } from '@/types';

export interface FilterAndSortButtonProps {
  children?: React.ReactNode;
  className?: string;
  onSortChange: (value: SortOptions | null) => void;
  onOpenChange?: (open: boolean) => void;
  sortOptions: SortOptions[];
  sortValue?: SortOptions;
  text: string;
  sortOnly?: boolean;
  /** Content rendered above the sort dropdown inside the filter panel. */
  beforeSort?: React.ReactNode;
  /**
   * Controls the open state externally. When provided, the component
   * becomes controlled and `onOpenChange` should be used to update the state.
   */
  isOpen?: boolean;
}

const FilterAndSortButton: React.FC<FilterAndSortButtonProps> = ({
  children,
  className,
  onSortChange,
  onOpenChange,
  sortOptions,
  sortValue,
  sortOnly,
  beforeSort,
  text,
  isOpen: propIsOpen,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const { t } = useTranslation();

  const isControlled = propIsOpen !== undefined;
  const isOpen = isControlled ? propIsOpen : internalIsOpen;

  const setIsOpen = (open: boolean) => {
    if (!isControlled) {
      setInternalIsOpen(open);
    }
    onOpenChange?.(open);
  };

  useLockBody(isOpen);

  return (
    <>
      {!isControlled && (
        <IconButton
          color="secondary"
          className={cn(
            'hover:border-grayscale-gray6! border-grayscale-gray2! shadow-elevation-1! dark:text-contrast! flex! aspect-square! h-12! items-center! justify-center! border! transition-colors!',
            className,
          )}
          onPress={() => setIsOpen(true)}
          data-testid="filter-and-sort-button"
        >
          <Filter size="xs" />
        </IconButton>
      )}
      <Surface
        className={cn(
          'fixed inset-0 z-1252 flex max-h-full flex-col items-center justify-start transition-transform duration-300 ease-out',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <IconX
          className="absolute top-6 right-6 size-4 cursor-pointer"
          onClick={() => setIsOpen(false)}
        />
        <Typography variant="h4" className="w-auto px-4 pt-16">
          {t('TrainingPlans.filtering.filterAndSort')}
        </Typography>
        <Surface className="flex w-full flex-1 flex-col items-start justify-start overflow-y-scroll! p-4 pb-24">
          {beforeSort}
          <Dropdown
            fullWidth
            getOptionId={option => option.id}
            getOptionText={option => option.translationString}
            label={`${t('TrainingPlans.filtering.sortBy')}:`}
            onChange={sortOptions => {
              onSortChange(sortOptions);
            }}
            options={sortOptions}
            renderOption={option => option.translationString}
            value={sortValue ?? null}
            className="text-left"
          />
          {children && !sortOnly && (
            <>
              <div className="my-4">
                <Typography fontWeight="bold" variant="body1">
                  {t('TrainingPlans.filtering.filters')}
                </Typography>
              </div>
              {children}
            </>
          )}
        </Surface>
        <Surface elevation={16} className="w-full p-4">
          <Button color="secondary" fullWidth onClick={() => setIsOpen(false)}>
            {text}
          </Button>
        </Surface>
      </Surface>
    </>
  );
};

export default FilterAndSortButton;
